import React from "react";
import Link from "next/link";

import NavBar from "@landing-components/NavBar/NavBar";
import Footer from "@landing-components/Footer/Footer";

import "./index.scss";
import "./privacy.scss";

const PrivacyPage = (props) => {
    return (
        <div className="privacy-page policy-page text-block">
            <NavBar theme="light" mode="access" border={true} />

            <div className="policy-page-content">
                <h1>Scribe privacy policy</h1>

                <p>
                    The privacy of your data — and it is your data, not ours! — is a big deal to us. We’ll
                    only ever access your account to help you with a problem or squash a software bug. We’ll
                    never open any uploaded files unless you ask us to. We log all access to all accounts by
                    IP address, so we can always verify that no unauthorized access has happened for as long
                    as the logs are kept.
                </p>

                <h2>Identity & access</h2>

                <p>
                    When you sign up for Scribe, we ask for your name, company name, and email address. That's
                    just so you can personalize your new account, and we can send you invoices, updates, or
                    other essential information. We’ll never sell your personal info to third parties, and we
                    won’t use your name or company in marketing statements without your permission, either.
                </p>

                <p>
                    When you pay for Scribe, we ask for your credit card and billing address. That's so we can
                    charge you for service, calculate taxes due, and send you invoices. Your credit card is
                    passed directly to our payment processor and doesn't ever go through our servers. We store
                    a record of the payment transaction, including the last 4 digits of the credit card
                    number, for account history, invoicing, and billing support. We store your billing address
                    to calculate any sales tax due in the United States, to detect fraudulent credit card
                    transactions, and to print on your invoices.
                </p>
                <p>
                    When you write Scribe with a question or to ask for help, we'll keep that correspondence,
                    and the email address, for future reference. When you browse our marketing pages, we'll
                    track that for statistical purposes (like conversion rates and to test new designs). We
                    also store any information you volunteer, like surveys, for as long as it makes sense.
                </p>
                <p>The only times we’ll ever share your info:</p>
                <ul>
                    <li>
                        To provide products or services you've requested, with your permission.{" "}
                        <Link href="/refunds" as="/refunds">
                            <a>List of third-party services we use</a>
                        </Link>
                        .
                    </li>
                    <li>
                        To investigate, prevent, or take action regarding illegal activities, suspected fraud,
                        situations involving potential threats to the physical safety of any person,
                        violations of our{" "}
                        <Link href="/terms-of-service" as="/terms-of-service">
                            <a>Terms of Service</a>
                        </Link>
                        , or as otherwise required by law.
                    </li>
                    <li>
                        If Scribe is acquired by or merged with another company — we don’t plan on that, but
                        if it happens — we’ll notify you well before any info about you is transferred and
                        becomes subject to a different privacy policy.
                    </li>
                </ul>

                <h2>Payment Processing</h2>

                <p>
                    We use Stripe for payment, analytics, and other business services. Stripe collects
                    identifying information about the devices that connect to its services. Stripe uses this
                    information to operate and improve the services it provides to us, including for fraud
                    detection. You can learn more about Stripe and read its privacy policy at{" "}
                    <a href="https://stripe.com/privacy">https://stripe.com/privacy</a>.
                </p>

                <h2>Law enforcement</h2>
                <p>
                    Scribe won’t hand your data over to law enforcement unless a court order says we have to.
                    We flat-out reject requests from local and federal law enforcement when they seek data
                    without a court order. And unless we're legally prevented from it, we’ll always inform you
                    when such requests are made.
                </p>

                <h2>Security & Encryption</h2>
                <p>
                    All data is encrypted via SSL/TLS when transmitted from our servers to your browser. The
                    database backups are also encrypted. Data isn’t encrypted while it's live in our database
                    (since it needs to be ready to send to you when you need it), but we go to great lengths
                    to secure your data at rest.
                </p>

                <h2>Deleted data</h2>
                <p>
                    When you cancel your account, we'll ensure that nothing is stored on our servers past 30
                    days. Anything you delete on your account while it's active will also be purged within 30
                    days (up until then it's available in the trash can).
                </p>

                <h2>Location of Site and Data</h2>
                <p>
                    This Site is operated in Australia. If you are located in the European Union or elsewhere
                    outside of the Australia, please be aware that any information you provide to us will be
                    transferred to the Australia. By using our Site, participating in any of our services
                    and/or providing us with your information, you consent to this transfer.
                </p>

                <h2>Changes & questions</h2>
                <p>
                    Scribe may update this policy once in a blue moon — we’ll notify you about significant
                    changes by emailing the account owner or by placing a prominent notice on our site. You
                    can access, change or delete your personal information at any time by contacting our{" "}
                    <a href="mailto: support@scribe.wiki">support team</a>.
                </p>

                <p>
                    Questions about this privacy policy? Please{" "}
                    <a href="mailto: support@scribe.wiki">get in touch</a> and we’ll be happy to answer them!
                </p>
            </div>

            <Footer />
        </div>
    );
};

export default PrivacyPage;
